import { api } from "@service/api";
import { Input } from "@components/forms/input";
import { Select } from "@components/forms/select";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { FaUserGroup } from "react-icons/fa6";
import { ISocialMediaTypeDTO } from "@hooks/dtos/ISocialMediaTypeDTO";
import { ICustomerSocialMediaDTO } from "@hooks/dtos/customer/ICustomerSocialMediaDTO";
import { CustomerSocialMediaStatusEnum } from "@hooks/dtos/customer/ICustomerSocialMediaDTO";
import { SocialMediaTypeIdEnum } from "@hooks/dtos/ISocialMediaTypeDTO";
import {
  getSocialMediaConfigBg,
  getSocialMediaConfigIcon,
} from "@components/social-media";
import { getCustomerSocialMediaStatus } from "@hooks/functions/SocialMedia/status";

interface IProps {
  isOpen: boolean;
  onClose: (updated?: boolean) => void;
}

// Form state interface
interface SocialMediaFormState {
  id?: number;
  type: number;
  url: string;
  status: string;
}

export function ModalSocialMediaEdit({ isOpen, onClose }: IProps) {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // UI state
  const [animate, setAnimate] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Data state
  const [socialMediaTypes, setSocialMediaTypes] = useState<
    ISocialMediaTypeDTO[]
  >([]);
  const [customerSocialMedias, setCustomerSocialMedias] = useState<
    ICustomerSocialMediaDTO[]
  >([]);

  // Form state
  const [formState, setFormState] = useState<SocialMediaFormState>({
    type: SocialMediaTypeIdEnum.INSTAGRAM,
    url: "",
    status: CustomerSocialMediaStatusEnum.ACTIVE,
  });
  const [isEditing, setIsEditing] = useState(false);

  // Status options
  const statusOptions = [
    { value: CustomerSocialMediaStatusEnum.ACTIVE, name: "Ativo" },
    { value: CustomerSocialMediaStatusEnum.INACTIVE, name: "Inativo" },
  ];

  // Fetch social media types
  const handleLoadSocialMediaTypes = async () => {
    try {
      const result = await api.get(`/misc/socialMediaTypes`);
      setSocialMediaTypes(result.data);
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data.message ??
          "Erro ao carregar tipos de rede social",
        type: "error",
        theme: "dark",
      });
    }
  };

  // Fetch customer social media
  const handleLoadCustomerSocialMedias = async () => {
    try {
      setIsLoading(true);
      const result = await api.get(`/customer/socialMedias`);
      setCustomerSocialMedias(result.data);
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data.message ?? "Erro ao carregar redes sociais",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Start editing a social media entry
  const handleEdit = (socialMedia: ICustomerSocialMediaDTO) => {
    setFormState({
      id: socialMedia.id,
      type: socialMedia.type,
      url: socialMedia.url,
      status: socialMedia.status,
    });
    setIsEditing(true);
  };

  // Start creating a new entry
  const handleAddNew = () => {
    setFormState({
      type: SocialMediaTypeIdEnum.INSTAGRAM,
      url: "",
      status: CustomerSocialMediaStatusEnum.ACTIVE,
    });
    setIsEditing(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // Validate form data
  const validateForm = (): boolean => {
    if (!formState.url.trim()) {
      pushToastify({
        message: "URL é obrigatória",
        type: "error",
        theme: "dark",
      });
      return false;
    }

    try {
      new URL(formState.url);
    } catch {
      pushToastify({
        message: "Digite uma URL válida",
        type: "error",
        theme: "dark",
      });
      return false;
    }

    // Check for duplicate active social media types
    const hasDuplicate = customerSocialMedias.some((sm) => {
      if (formState.id && sm.id === formState.id) return false; // Skip current being edited
      return (
        sm.type === formState.type &&
        sm.status === CustomerSocialMediaStatusEnum.ACTIVE
      );
    });

    if (
      hasDuplicate &&
      formState.status === CustomerSocialMediaStatusEnum.ACTIVE
    ) {
      pushToastify({
        message: "Já existe uma rede social ativa deste tipo",
        type: "error",
        theme: "dark",
      });
      return false;
    }

    return true;
  };

  // Save social media entry
  const handleSave = async () => {
    if (!validateForm()) return;

    Swal.fire({
      title: "Salvar alterações?",
      text: "Deseja salvar as alterações?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, salvar!",
      cancelButtonText: "Cancelar",
    }).then(async (result: { isConfirmed: boolean }) => {
      if (result.isConfirmed) {
        try {
          if (formState.id) {
            // Update existing
            await api.put(`/customer/socialMedia`, {
              customerSocialMediaId: formState.id,
              type: formState.type,
              url: formState.url,
              status: formState.status,
            });
          } else {
            // Create new
            await api.post(`/customer/socialMedia`, {
              type: formState.type,
              url: formState.url,
              status: formState.status,
            });
          }

          pushToastify({
            message: "Rede social salva com sucesso!",
            type: "success",
            theme: "dark",
          });

          await handleLoadCustomerSocialMedias();
          setIsEditing(false);
          setUpdated(true);
        } catch (err: any) {
          pushToastify({
            message:
              err?.response?.data.message ?? "Erro ao salvar rede social",
            type: "error",
            theme: "dark",
          });
        }
      }
    });
  };

  // Close modal
  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      onClose(updated);
      setIsEditing(false);
    }, 300);
  };

  // Handle escape key
  const handleEscape = (evt: KeyboardEvent) => {
    if (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27) {
      if (isEditing) {
        handleCancelEdit();
      } else {
        handleClose();
      }
    }
  };

  // Get available social media types (excluding already active ones)
  const getAvailableTypes = () => {
    return socialMediaTypes.filter((type) => {
      if (isEditing && formState.id && formState.type === type.id) return true; // Include current when editing
      const hasActive = customerSocialMedias.some(
        (sm) =>
          sm.type === type.id &&
          sm.status === CustomerSocialMediaStatusEnum.ACTIVE,
      );
      return !hasActive;
    });
  };

  // Initialize
  useEffect(() => {
    if (isOpen) {
      Promise.all([
        handleLoadSocialMediaTypes(),
        handleLoadCustomerSocialMedias(),
      ]);
      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`modal fade ${animate ? "show" : ""}`}
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold">Perfis Sociais</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
              ></button>
            </div>

            <div className="modal-body pt-2">
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              ) : isEditing ? (
                // Edit/Create form
                <div className="p-3">
                  <h6 className="mb-4 fw-semibold">
                    {formState.id
                      ? "Editar Perfil Social"
                      : "Novo Perfil Social"}
                  </h6>

                  <div className="mb-3">
                    <Select
                      animated
                      label="Tipo de Rede Social"
                      value={formState.type.toString()}
                      options={getAvailableTypes().map((type) => ({
                        value: type.id.toString(),
                        name: type.name,
                      }))}
                      onChange={(e) =>
                        setFormState({
                          ...formState,
                          type: parseInt(e.target.value),
                        })
                      }
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      animated
                      label="URL do Perfil"
                      placeholder="https://exemplo.com/usuario"
                      value={formState.url}
                      onChange={(e) =>
                        setFormState({ ...formState, url: e.target.value })
                      }
                    />
                  </div>

                  <div className="mb-4">
                    <Select
                      animated
                      label="Status"
                      value={formState.status}
                      options={statusOptions}
                      onChange={(e) =>
                        setFormState({ ...formState, status: e.target.value })
                      }
                    />
                  </div>

                  <div className="d-flex gap-2 mt-4">
                    <button
                      className="btn btn-success flex-fill"
                      onClick={handleSave}
                    >
                      {formState.id ? "Atualizar" : "Adicionar"}
                    </button>
                    <button
                      className="btn btn-secondary flex-fill"
                      onClick={handleCancelEdit}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                // List view
                <>
                  <div className="mb-3 px-3">
                    <button
                      className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                      onClick={handleAddNew}
                    >
                      <span style={{ fontSize: "1.2rem" }}>+</span>
                      Adicionar Perfil Social
                    </button>
                  </div>

                  {customerSocialMedias.length > 0 ? (
                    <div className="px-3">
                      <div className="d-flex flex-column">
                        {customerSocialMedias.map((sm) => {
                          const status = getCustomerSocialMediaStatus(
                            sm.status,
                          );
                          return (
                            <div
                              key={sm.id}
                              className="card border-0 shadow-sm mb-3"
                              title={
                                sm?.typeData?.name ||
                                "Rede Social não identificada"
                              }
                              style={{
                                backgroundColor: getSocialMediaConfigBg(
                                  sm.type,
                                ),
                                transition: "transform 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform =
                                  "translateY(-2px)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform =
                                  "translateY(0)";
                              }}
                            >
                              <div className="card-body">
                                <div className="d-flex align-items-center gap-3">
                                  <div className="d-flex flex-column align-items-center gap-2">
                                    <div
                                      className="d-flex align-items-center justify-content-center rounded-3"
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        backgroundColor: "white",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {getSocialMediaConfigIcon(sm.type)}
                                    </div>
                                    <span
                                      className={`position-absolute end-1 bottom-0 badge bg-${status.color} mb-1`}
                                      style={{ fontSize: "0.8rem" }}
                                      title={status.description}
                                    >
                                      {status.name}
                                    </span>
                                  </div>

                                  <div className="flex-grow-1 min-w-0">
                                    <a
                                      href={sm.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-muted text-decoration-none d-block text-truncate"
                                      style={{
                                        maxWidth: "100%",
                                        fontSize: "0.85rem",
                                      }}
                                    >
                                      <div
                                        className="bg-white border border-1 rounded p-2 fs-6"
                                        title={sm.url}
                                      >
                                        {sm.url}
                                      </div>
                                    </a>
                                  </div>

                                  <div className="d-flex gap-2 flex-shrink-0">
                                    <button
                                      className="btn btn-sm btn-primary rounded"
                                      onClick={() => handleEdit(sm)}
                                      style={{ minWidth: "70px" }}
                                    >
                                      Editar
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <div className="mb-3 text-muted">
                        <FaUserGroup size={64} className="text-muted" />
                      </div>
                      <p className="text-muted mb-0">
                        Nenhuma rede social cadastrada
                      </p>
                      <p className="text-muted small">
                        Clique no botão acima para adicionar
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {!isEditing && !isLoading && (
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-secondary" onClick={handleClose}>
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`modal-backdrop fade ${animate ? "show" : ""}`}></div>
    </>
  );
}
